import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, Printer, Trophy, Share2, X as XIcon, Facebook, MessageCircle, Copy, Check, ImageIcon } from "lucide-react";
import { Section, TeamLogo } from "../components/UIBits";
import ShareCardModal from "../components/ShareCardModal";
import { phaseLabel, LOGO_URL_SOCIAL } from "../lib/constants";
import { useT, formatDayLocalized } from "../lib/i18n";
import api from "../lib/api";

export default function MatchSummaryPage() {
    const { t, lang } = useT();
    const { num } = useParams();
    const [game, setGame] = useState(null);
    const [teams, setTeams] = useState([]);
    const [events, setEvents] = useState([]);
    const [players, setPlayers] = useState([]);
    const [stats, setStats] = useState([]);
    const [shareOpen, setShareOpen] = useState(false);
    const [shareCardOpen, setShareCardOpen] = useState(false);

    useEffect(() => {
        api.get(`/games/${num}`).then(r => setGame(r.data));
        api.get(`/teams?light=true`).then(r => setTeams(r.data));
        api.get(`/games/${num}/events`).then(r => setEvents(r.data)).catch(() => {});
        api.get(`/players`).then(r => setPlayers(r.data));
        api.get(`/player_stats/${num}`).then(r => setStats(r.data));
    }, [num]);

    const teamMap = useMemo(() => Object.fromEntries(teams.map(x => [x.id, x])), [teams]);
    const playerMap = useMemo(() => Object.fromEntries(players.map(p => [p.id, p])), [players]);

    if (!game) return <Section><div className="text-white/50">{t("common.loading") || "Chargement..."}</div></Section>;

    const home = teamMap[game.home_team_id];
    const away = teamMap[game.away_team_id];

    // Sharing metadata + helpers
    const homeName = home?.name || game.home_placeholder || "Locale";
    const awayName = away?.name || game.away_placeholder || "Visiteur";
    const shareUrl = typeof window !== "undefined" ? window.location.href : `https://defiultime.com/match/${num}/sommaire`;
    const shareTitle = `${homeName} ${game.home_score} — ${game.away_score} ${awayName}`;
    const shareText = `Sommaire · Partie #${game.game_num} · ${game.category}${game.phase && game.phase !== "preliminaire" ? ` · ${phaseLabel(game.phase, lang)}` : ""} — Défi Ultime`;

    const nativeShare = async () => {
        if (typeof navigator !== "undefined" && navigator.share) {
            try {
                await navigator.share({ title: shareTitle, text: shareText, url: shareUrl });
            } catch { /* user cancelled */ }
        } else {
            setShareOpen(true);
        }
    };

    // Group events by period
    const byPeriod = {};
    events.forEach(ev => {
        const p = ev.period || 1;
        byPeriod[p] = byPeriod[p] || { goals: [], penalties: [] };
        if (ev.type === "goal") byPeriod[p].goals.push(ev);
        else if (ev.type === "penalty") byPeriod[p].penalties.push(ev);
    });
    const periods = Object.keys(byPeriod).map(Number).sort((a, b) => a - b);

    // Per-team player stats (only players with any activity)
    const rosterStats = (teamId) => {
        const rosterIds = new Set(players.filter(p => p.team_id === teamId).map(p => p.id));
        return stats
            .filter(s => rosterIds.has(s.player_id))
            .filter(s => (s.goals || 0) + (s.assists || 0) + (s.pim || 0) > 0)
            .map(s => ({ ...s, player: playerMap[s.player_id] }))
            .filter(s => s.player)
            .sort((a, b) => (b.goals + b.assists) - (a.goals + a.assists));
    };
    const homeStats = rosterStats(game.home_team_id);
    const awayStats = rosterStats(game.away_team_id);

    const winner =
        game.status === "final"
            ? (game.home_score > game.away_score ? "home" : game.home_score < game.away_score ? "away" : "tie")
            : null;

    return (
        <div data-testid="match-summary" className="print:bg-white print:text-black">
            <Helmet>
                <title>{`Sommaire #${num} — ${homeName} vs ${awayName}`}</title>
                <meta property="og:type" content="article" />
                <meta property="og:title" content={`${shareTitle} — Défi Ultime`} />
                <meta property="og:description" content={shareText} />
                <meta property="og:url" content={shareUrl} />
                <meta property="og:image" content={LOGO_URL_SOCIAL} />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={`${shareTitle} — Défi Ultime`} />
                <meta name="twitter:description" content={shareText} />
                <meta name="twitter:image" content={LOGO_URL_SOCIAL} />
            </Helmet>
            <Section>
                <div className="flex flex-wrap items-center justify-between gap-2 mb-6 print:hidden">
                    <Link to={`/match/${num}`} className="inline-flex items-center gap-2 text-[#4FE0D5] hover:text-[#7FD93C] text-sm"><ArrowLeft size={14} /> Retour à la partie</Link>
                    <div className="flex gap-2 flex-wrap">
                        <button onClick={() => setShareCardOpen(true)} className="btn-primary flex items-center gap-2" data-testid="btn-share-card"><ImageIcon size={14} /> Partager le résultat</button>
                        <button onClick={nativeShare} className="btn-ghost flex items-center gap-2" data-testid="btn-share"><Share2 size={14} /> Partager le lien</button>
                        <button onClick={() => window.print()} className="btn-ghost flex items-center gap-2" data-testid="btn-print"><Printer size={14} /> Imprimer</button>
                    </div>
                </div>

                {/* Header */}
                <div className="glass-card p-4 sm:p-8 mb-6 print:border print:border-black" data-testid="summary-header">
                    <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 mb-4 text-sm sm:text-base">
                        <span className="text-[#4FE0D5] font-bold uppercase tracking-widest">Partie #{game.game_num}</span>
                        <span className="text-white/30">•</span>
                        <span className="font-semibold">{game.category}</span>
                        {game.phase && game.phase !== "preliminaire" && (
                            <>
                                <span className="text-white/30">•</span>
                                <span className="px-3 py-1 bg-[#4FE0D5]/15 text-[#4FE0D5] font-bold text-xs uppercase tracking-widest">{phaseLabel(game.phase, lang)}</span>
                            </>
                        )}
                    </div>
                    <div className="text-center text-sm sm:text-base text-white/60 mb-6">
                        {formatDayLocalized(game.day, lang)} · {game.time} · {game.ice}
                    </div>

                    <div className="grid grid-cols-3 items-center gap-2 sm:gap-6 mt-4">
                        <div className="flex flex-col items-center gap-2 sm:gap-3">
                            <TeamLogo team={home} size={100} />
                            <div className="font-display text-xl sm:text-3xl md:text-4xl uppercase text-center leading-tight break-words w-full">{home?.name || game.home_placeholder || "TBD"}</div>
                            {winner === "home" && <span className="px-2 py-1 bg-[#7FD93C] text-black text-xs font-bold uppercase tracking-widest flex items-center gap-1"><Trophy size={12} /> Vainqueur</span>}
                        </div>
                        <div className="text-center">
                            <div className="font-display text-6xl sm:text-8xl md:text-9xl leading-none">
                                <span className={winner === "home" ? "text-[#7FD93C]" : ""}>{game.home_score}</span>
                                <span className="text-white/20 mx-1 sm:mx-3">—</span>
                                <span className={winner === "away" ? "text-[#7FD93C]" : ""}>{game.away_score}</span>
                            </div>
                            <div className={`mt-3 sm:mt-4 inline-block px-3 py-1 text-xs sm:text-sm font-bold uppercase tracking-widest ${game.status === "final" ? "bg-white/10 text-white/70" : game.status === "live" ? "bg-[#7FD93C] text-black animate-pulse" : "bg-[#4FE0D5]/20 text-[#4FE0D5]"}`}>
                                {game.status === "final" ? "Terminée" : game.status === "live" ? "En direct" : "À venir"}
                            </div>
                        </div>
                        <div className="flex flex-col items-center gap-2 sm:gap-3">
                            <TeamLogo team={away} size={100} />
                            <div className="font-display text-xl sm:text-3xl md:text-4xl uppercase text-center leading-tight break-words w-full">{away?.name || game.away_placeholder || "TBD"}</div>
                            {winner === "away" && <span className="px-2 py-1 bg-[#7FD93C] text-black text-xs font-bold uppercase tracking-widest flex items-center gap-1"><Trophy size={12} /> Vainqueur</span>}
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3 mt-6 sm:mt-8">
                        <div className="bg-black/40 p-3 sm:p-4 print:border print:border-black">
                            <div className="text-white/50 uppercase tracking-widest text-[10px] sm:text-xs mb-2 truncate">{home?.name || "Local"}</div>
                            <div className="grid grid-cols-3 gap-1 text-center">
                                <div><div className="font-display text-2xl sm:text-3xl text-white">{game.home_score}</div><div className="text-[9px] sm:text-[10px] text-white/40 uppercase">Buts</div></div>
                                <div><div className="font-display text-2xl sm:text-3xl text-white">{game.home_shots || 0}</div><div className="text-[9px] sm:text-[10px] text-white/40 uppercase">Tirs</div></div>
                                <div><div className="font-display text-2xl sm:text-3xl text-rose-400">{game.home_pim || 0}</div><div className="text-[9px] sm:text-[10px] text-white/40 uppercase">PIM</div></div>
                            </div>
                        </div>
                        <div className="bg-black/40 p-3 sm:p-4 print:border print:border-black">
                            <div className="text-white/50 uppercase tracking-widest text-[10px] sm:text-xs mb-2 truncate">{away?.name || "Visiteur"}</div>
                            <div className="grid grid-cols-3 gap-1 text-center">
                                <div><div className="font-display text-2xl sm:text-3xl text-white">{game.away_score}</div><div className="text-[9px] sm:text-[10px] text-white/40 uppercase">Buts</div></div>
                                <div><div className="font-display text-2xl sm:text-3xl text-white">{game.away_shots || 0}</div><div className="text-[9px] sm:text-[10px] text-white/40 uppercase">Tirs</div></div>
                                <div><div className="font-display text-2xl sm:text-3xl text-rose-400">{game.away_pim || 0}</div><div className="text-[9px] sm:text-[10px] text-white/40 uppercase">PIM</div></div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Scoring & penalties by period */}
                {periods.length > 0 ? (
                    periods.map(p => {
                        const grp = byPeriod[p];
                        return (
                            <div key={p} className="glass-card p-4 sm:p-6 mb-4 print:border print:border-black" data-testid={`period-${p}`}>
                                <h3 className="font-display text-2xl sm:text-3xl uppercase tracking-widest mb-4 pb-2 border-b border-white/10">
                                    {p === 4 ? "Prolongation" : `Période ${p}`}
                                </h3>
                                {grp.goals.length > 0 && (
                                    <div className="mb-4">
                                        <div className="label-mini mb-2 text-[#7FD93C]">Buts</div>
                                        <div className="space-y-2">
                                            {grp.goals.map(ev => {
                                                const scorer = playerMap[ev.player_id];
                                                const a1 = ev.assist1_id ? playerMap[ev.assist1_id] : null;
                                                const a2 = ev.assist2_id ? playerMap[ev.assist2_id] : null;
                                                return (
                                                    <div key={ev.id} className="flex flex-wrap sm:flex-nowrap items-baseline gap-2 sm:gap-3 py-2 border-b border-white/5 last:border-0" data-testid={`goal-${ev.id}`}>
                                                        <span className="w-14 sm:w-16 font-mono text-white/50 text-sm sm:text-base shrink-0">{ev.time || "--:--"}</span>
                                                        <span className={`px-2 py-0.5 text-[10px] sm:text-xs font-bold shrink-0 uppercase tracking-wider ${ev.team === "home" ? "bg-[#7FD93C]/20 text-[#7FD93C]" : "bg-[#4FE0D5]/20 text-[#4FE0D5]"}`}>
                                                            {ev.team === "home" ? (home?.name || "LOC") : (away?.name || "VIS")}
                                                        </span>
                                                        <div className="flex-1 min-w-0 basis-full sm:basis-auto">
                                                            <div className="font-semibold text-base sm:text-lg">
                                                                {scorer ? <>#{scorer.number} <span>{scorer.name}</span></> : "But"}
                                                            </div>
                                                            {(a1 || a2) && (
                                                                <div className="text-white/50 text-xs sm:text-sm mt-0.5">
                                                                    Aides : {[a1, a2].filter(Boolean).map(x => `#${x.number} ${x.name}`).join(", ")}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                                {grp.penalties.length > 0 && (
                                    <div>
                                        <div className="label-mini mb-2 text-rose-400">Pénalités</div>
                                        <div className="space-y-2">
                                            {grp.penalties.map(ev => {
                                                const pen = playerMap[ev.player_id];
                                                const cancelled = !!ev.cancelled;
                                                return (
                                                    <div key={ev.id} className={`flex flex-wrap sm:flex-nowrap items-baseline gap-2 sm:gap-3 py-2 border-b border-white/5 last:border-0 ${cancelled ? "opacity-60" : ""}`} data-testid={`penalty-${ev.id}`}>
                                                        <span className="w-14 sm:w-16 font-mono text-white/50 text-sm sm:text-base shrink-0">{ev.time || "--:--"}</span>
                                                        <span className={`px-2 py-0.5 text-[10px] sm:text-xs font-bold shrink-0 uppercase tracking-wider ${ev.team === "home" ? "bg-[#7FD93C]/20 text-[#7FD93C]" : "bg-[#4FE0D5]/20 text-[#4FE0D5]"}`}>
                                                            {ev.team === "home" ? (home?.name || "LOC") : (away?.name || "VIS")}
                                                        </span>
                                                        <div className="flex-1 min-w-0 basis-full sm:basis-auto">
                                                            <div className={`font-semibold text-base sm:text-lg ${cancelled ? "line-through" : ""}`}>
                                                                {pen ? <>#{pen.number} <span>{pen.name}</span></> : "Pénalité"}
                                                                <span className="text-rose-400 ml-2">{ev.minutes || 0} min</span>
                                                            </div>
                                                            {ev.reason && <div className={`text-white/50 text-xs sm:text-sm mt-0.5 ${cancelled ? "line-through" : ""}`}>{ev.reason}</div>}
                                                            {cancelled && (
                                                                <div className="text-rose-300 text-xs sm:text-sm mt-0.5 flex items-center gap-1">
                                                                    <span className="px-1.5 py-0.5 bg-rose-500/20 text-[10px] font-bold uppercase tracking-widest">Annulée</span>
                                                                    <span className="text-white/50">
                                                                        {ev.cancelled_reason || "But marqué contre l'équipe punie"}
                                                                        {ev.cancelled_at_clock && ev.cancelled_at_period && ` — P${ev.cancelled_at_period} ${ev.cancelled_at_clock}`}
                                                                    </span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                                {grp.goals.length === 0 && grp.penalties.length === 0 && (
                                    <div className="text-white/40 text-sm italic">Aucun événement enregistré.</div>
                                )}
                            </div>
                        );
                    })
                ) : (
                    <div className="glass-card p-6 text-center text-white/50 mb-6" data-testid="no-events">Aucun événement enregistré pour cette partie.</div>
                )}

                {/* Player stats */}
                <div className="grid md:grid-cols-2 gap-4 mt-2">
                    <StatsTable title={home?.name || "Local"} rows={homeStats} testId="home-stats" />
                    <StatsTable title={away?.name || "Visiteur"} rows={awayStats} testId="away-stats" />
                </div>
            </Section>

            {shareOpen && (
                <ShareModal
                    url={shareUrl}
                    title={shareTitle}
                    text={shareText}
                    onClose={() => setShareOpen(false)}
                />
            )}

            <ShareCardModal
                open={shareCardOpen}
                onClose={() => setShareCardOpen(false)}
                game={game}
                home={home}
                away={away}
                homeName={homeName}
                awayName={awayName}
                phaseText={game.phase && game.phase !== "preliminaire" ? phaseLabel(game.phase, lang) : ""}
                dateText={formatDayLocalized(game.day, lang)}
            />
        </div>
    );
}

function ShareModal({ url, title, text, onClose }) {
    const [copied, setCopied] = useState(false);
    const encoded = encodeURIComponent(url);
    const encodedText = encodeURIComponent(`${title} — ${text}`);
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(url);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch { /* clipboard denied */ }
    };
    return (
        <div className="fixed inset-0 bg-black/85 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="share-modal">
            <div onClick={e => e.stopPropagation()} className="bg-[#0a0a0a] border border-white/10 w-full max-w-sm">
                <div className="p-4 border-b border-white/10 flex items-center justify-between">
                    <h2 className="font-display text-xl uppercase tracking-widest">Partager</h2>
                    <button onClick={onClose} className="text-white/60 hover:text-white"><XIcon size={20} /></button>
                </div>
                <div className="p-5 space-y-2">
                    <a
                        href={`https://www.facebook.com/sharer/sharer.php?u=${encoded}`}
                        target="_blank"
                        rel="noreferrer"
                        className="w-full py-3 px-4 flex items-center gap-3 bg-[#1877F2] text-white hover:opacity-90"
                        data-testid="share-facebook"
                    >
                        <Facebook size={18} /> Facebook
                    </a>
                    <a
                        href={`https://www.facebook.com/dialog/send?app_id=140586622674265&link=${encoded}&redirect_uri=${encoded}`}
                        target="_blank"
                        rel="noreferrer"
                        className="w-full py-3 px-4 flex items-center gap-3 bg-[#0084FF] text-white hover:opacity-90"
                        data-testid="share-messenger"
                    >
                        <MessageCircle size={18} /> Messenger
                    </a>
                    <a
                        href={`https://wa.me/?text=${encodedText}%20${encoded}`}
                        target="_blank"
                        rel="noreferrer"
                        className="w-full py-3 px-4 flex items-center gap-3 bg-[#25D366] text-white hover:opacity-90"
                        data-testid="share-whatsapp"
                    >
                        <MessageCircle size={18} /> WhatsApp
                    </a>
                    <a
                        href={`https://twitter.com/intent/tweet?text=${encodedText}&url=${encoded}`}
                        target="_blank"
                        rel="noreferrer"
                        className="w-full py-3 px-4 flex items-center gap-3 bg-black border border-white/20 text-white hover:bg-white/10"
                        data-testid="share-twitter"
                    >
                        <XIcon size={18} /> X (Twitter)
                    </a>
                    <button
                        onClick={copy}
                        className={`w-full py-3 px-4 flex items-center gap-3 border transition ${copied ? "bg-[#7FD93C]/20 border-[#7FD93C] text-[#7FD93C]" : "border-white/20 text-white/80 hover:bg-white/5"}`}
                        data-testid="share-copy"
                    >
                        {copied ? <><Check size={18} /> Lien copié !</> : <><Copy size={18} /> Copier le lien</>}
                    </button>
                </div>
            </div>
        </div>
    );
}

function StatsTable({ title, rows, testId }) {
    return (
        <div className="glass-card p-4 sm:p-5 print:border print:border-black" data-testid={testId}>
            <h3 className="font-display text-xl sm:text-2xl uppercase tracking-widest mb-3 sm:mb-4 pb-2 border-b border-white/10 truncate">{title}</h3>
            {rows.length === 0 ? (
                <div className="text-white/40 text-sm sm:text-base italic">Aucun joueur avec activité enregistrée.</div>
            ) : (
                <table className="w-full">
                    <thead>
                        <tr className="text-[10px] sm:text-xs uppercase tracking-widest text-white/50 border-b border-white/10">
                            <th className="text-left py-2">Joueur</th>
                            <th className="text-center py-2 w-10">B</th>
                            <th className="text-center py-2 w-10">A</th>
                            <th className="text-center py-2 w-10">P</th>
                            <th className="text-center py-2 w-12">PIM</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map(r => {
                            const pts = (r.goals || 0) + (r.assists || 0);
                            return (
                                <tr key={r.player_id} className="border-b border-white/5 last:border-0">
                                    <td className="py-2 text-sm sm:text-base font-semibold">
                                        <span className="text-white/50 text-xs mr-1">#{r.player.number}</span>
                                        {r.player.name}
                                    </td>
                                    <td className="text-center py-2 text-base sm:text-lg">{r.goals || 0}</td>
                                    <td className="text-center py-2 text-base sm:text-lg">{r.assists || 0}</td>
                                    <td className="text-center py-2 text-lg sm:text-xl font-bold text-[#4FE0D5]">{pts}</td>
                                    <td className="text-center py-2 text-sm sm:text-base text-rose-300">{r.pim || 0}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            )}
        </div>
    );
}
